import React, { useState, useEffect, useRef } from "react";
import "./KitchenPrice.css";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
import { FaEdit, FaTrash } from "react-icons/fa";
import moment from "moment-timezone";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Switch,
  DatePicker,
  FormControlLabel,
  Paper,
  useMediaQuery,
  IconButton,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const KitchenPrice = ({ editMode, kitchenData, updateKitchenData }) => {
  // Local state for pricing fields
  const [pricingData, setPricingData] = useState({
    bookingType: kitchenData.bookingType || "hour",
    hourlyRate: kitchenData.hourlyRate || "",
    dayRate: kitchenData.dayRate || "",
    cleaningFee: kitchenData.cleaningFee || "",
    minBooking: kitchenData.minBooking || "",
    securityDeposit: kitchenData.securityDeposit || "",
    rateEntries: kitchenData.rateEntries || [],
    blockedDates: kitchenData.blockedDates || [],
  });

  const initializedRef = useRef(false);

  // Initialize local state from parent's kitchenData in edit mode
  useEffect(() => {
    if (editMode && !initializedRef.current && kitchenData.KitchenID) {
      setPricingData({
        bookingType: kitchenData.bookingType || "hour",
        hourlyRate: kitchenData.hourlyRate || "",
        dayRate: kitchenData.dayRate || "",
        cleaningFee: kitchenData.cleaningFee || "",
        minBooking: kitchenData.minBooking || "",
        securityDeposit: kitchenData.securityDeposit || "",
        rateEntries: kitchenData.rateEntries || [],
        blockedDates: kitchenData.blockedDates || [],
      });
      initializedRef.current = true;
    }
  }, [editMode, kitchenData]);

  // Push local pricingData changes up to the parent's state
  useEffect(() => {
    updateKitchenData({ ...pricingData });
  }, [pricingData, updateKitchenData]);

  // Local state for managing new availability slot
  const [slotData, setSlotData] = useState({
    newStartDate: null,
    newEndDate: null,
    newAvailabilityPrice: "",
    newRecurring: false,
    editingSlotIndex: null,
  });

  // Check if the new slot overlaps existing slots
  const isAvailabilityOverlap = (newSlot, excludeIndex = null) => {
    const newStart = new Date(newSlot.startDateTime).getTime();
    const newEnd = new Date(newSlot.endDateTime).getTime();
    return pricingData.rateEntries.some((slot, index) => {
      if (index === excludeIndex) return false;
      const slotStart = new Date(slot.startDateTime).getTime();
      const slotEnd = new Date(slot.endDateTime).getTime();
      return newStart < slotEnd && newEnd > slotStart;
    });
  };

  // Handler for adding/updating an availability slot
  const handleAddOrUpdateSlot = () => {
    const {
      newStartDate,
      newEndDate,
      newAvailabilityPrice,
      editingSlotIndex,
      newRecurring,
    } = slotData;

    if (!newStartDate || !newEndDate || !newAvailabilityPrice) {
      alert("Please select start/end times and a price.");
      return;
    }
    if (newEndDate <= newStartDate) {
      alert("End date/time must be after start date/time.");
      return;
    }
    const price = parseFloat(newAvailabilityPrice);
    if (isNaN(price) || price <= 0) {
      alert("Price must be a number greater than 0.");
      return;
    }

    // Convert times to UTC based on the kitchen's timezone using moment-timezone.
    const timeZone =
      kitchenData.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startUtc = moment.tz(newStartDate, timeZone).utc().toISOString();
    const endUtc = moment.tz(newEndDate, timeZone).utc().toISOString();

    const slot = {
      startDateTime: startUtc,
      endDateTime: endUtc,
      price,
      recurring: newRecurring,
    };

    if (newRecurring) {
      // Store both start and end weekday names.
      const startDay = newStartDate.toLocaleDateString("en-US", {
        weekday: "long",
      });
      const endDay = newEndDate.toLocaleDateString("en-US", {
        weekday: "long",
      });
      slot.startDay = startDay;
      slot.endDay = endDay;
      // Calculate duration in milliseconds (used internally)
      slot.duration = newEndDate.getTime() - newStartDate.getTime();
    }

    if (editingSlotIndex !== null) {
      if (isAvailabilityOverlap(slot, editingSlotIndex)) {
        alert("The updated time slot overlaps with another slot.");
        return;
      }
      const updatedEntries = [...pricingData.rateEntries];
      updatedEntries[editingSlotIndex] = slot;
      setPricingData({ ...pricingData, rateEntries: updatedEntries });
      setSlotData({
        newStartDate: null,
        newEndDate: null,
        newAvailabilityPrice: "",
        newRecurring: false,
        editingSlotIndex: null,
      });
    } else {
      if (isAvailabilityOverlap(slot)) {
        alert("This time slot overlaps with an existing slot.");
        return;
      }
      setPricingData({
        ...pricingData,
        rateEntries: [...pricingData.rateEntries, slot],
      });
      setSlotData({
        newStartDate: null,
        newEndDate: null,
        newAvailabilityPrice: "",
        newRecurring: false,
        editingSlotIndex: null,
      });
    }
  };

  const handleEditSlot = (index) => {
    const slot = pricingData.rateEntries[index];
    setSlotData({
      newStartDate: new Date(slot.startDateTime),
      newEndDate: new Date(slot.endDateTime),
      newAvailabilityPrice: slot.price.toString(),
      newRecurring: slot.recurring,
      editingSlotIndex: index,
    });
  };

  const handleRemoveSlot = (index) => {
    const updatedEntries = pricingData.rateEntries.filter(
      (_, i) => i !== index
    );
    setPricingData({ ...pricingData, rateEntries: updatedEntries });
    if (slotData.editingSlotIndex === index) {
      setSlotData({
        newStartDate: null,
        newEndDate: null,
        newAvailabilityPrice: "",
        newRecurring: false,
        editingSlotIndex: null,
      });
    }
  };

  const handleCancelEdit = () => {
    setSlotData({
      newStartDate: null,
      newEndDate: null,
      newAvailabilityPrice: "",
      newRecurring: false,
      editingSlotIndex: null,
    });
  };

  const handleChange = (field, value) => {
    setPricingData({ ...pricingData, [field]: value });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Box
      sx={{
        p: isMobile ? 1 : 4,
        background: "#fff",
      }}
    >
      <Typography variant="h6" gutterBottom>
        {pricingData.bookingType === "hour" ? "Hourly Rate" : "Day Rate"}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Hourly Rate"
            fullWidth
            type="number"
            value={pricingData.hourlyRate}
            onChange={(e) => handleChange("hourlyRate", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Cleaning Fee"
            fullWidth
            type="number"
            value={pricingData.cleaningFee}
            onChange={(e) => handleChange("cleaningFee", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Min Booking"
            fullWidth
            type="number"
            value={pricingData.minBooking}
            onChange={(e) => handleChange("minBooking", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Security Deposit"
            fullWidth
            type="number"
            value={pricingData.securityDeposit}
            onChange={(e) => handleChange("securityDeposit", e.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" gutterBottom>
        Set Availability
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              value={slotData.newStartDate}
              onChange={(date) =>
                setSlotData({ ...slotData, newStartDate: date })
              }
              showTimeSelect={pricingData.bookingType === "hour"}
              dateFormat={pricingData.bookingType === "hour" ? "Pp" : "P"}
              placeholderText="Start Time"
              className="react-datepicker-wrapper"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              value={slotData.newEndDate}
              onChange={(date) =>
                setSlotData({ ...slotData, newEndDate: date })
              }
              showTimeSelect={pricingData.bookingType === "hour"}
              dateFormat={pricingData.bookingType === "hour" ? "Pp" : "P"}
              placeholderText="End Time"
              className="react-datepicker-wrapper"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Rate"
            fullWidth
            type="number"
            value={slotData.newAvailabilityPrice}
            onChange={(e) =>
              setSlotData({ ...slotData, newAvailabilityPrice: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={slotData.newRecurring}
                onChange={(e) =>
                  setSlotData({ ...slotData, newRecurring: e.target.checked })
                }
              />
            }
            label="Set Weekly"
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleAddOrUpdateSlot}
            sx={{ backgroundColor: "#2c3e50", mr: 2 }}
          >
            {slotData.editingSlotIndex !== null ? "Update" : "Add"}
          </Button>
          {slotData.editingSlotIndex !== null && (
            <Button variant="outlined" onClick={handleCancelEdit} color="error">
              Cancel
            </Button>
          )}
        </Grid>
      </Grid>

      {pricingData.rateEntries.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6">Availability Slots</Typography>
          {pricingData.rateEntries.map((slot, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                my: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>
                {slot.recurring
                  ? `Every week from ${slot.startDay} to ${slot.endDay} at $${slot.price}`
                  : `${new Date(
                      slot.startDateTime
                    ).toLocaleString()} - ${new Date(
                      slot.endDateTime
                    ).toLocaleString()} ($${slot.price})`}
              </Typography>
              <Box>
                <IconButton
                  onClick={() => handleEditSlot(index)}
                  color="primary"
                >
                  <FaEdit />
                </IconButton>
                <IconButton
                  onClick={() => handleRemoveSlot(index)}
                  color="error"
                >
                  <FaTrash />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default KitchenPrice;
